import React from "react";
import {
  ClockIcon,
  PackageIcon,
  StoreIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "lucide-react";
import {
  DAILY_ORDERS_LABEL,
  STORE_ORDERS_LABEL,
  storeSlotExpandKey,
  type DeliveryOrderPartition,
} from "../utils/deliveryTimeSlots";

type SlotSectionProps<T> = {
  partition: DeliveryOrderPartition<T>;
  expandedSlots: Set<string>;
  onToggleSlot: (key: string) => void;
  renderOrder: (item: T) => React.ReactNode;
  /** Which partition to render; default shows both stacked sections */
  sections?: "all" | "daily" | "store";
  dailySectionClassName?: string;
  storeSectionClassName?: string;
};

function CollapsibleHeader({
  title,
  count,
  isExpanded,
  icon,
  onClick,
  className = "",
}: {
  title: string;
  count: number;
  isExpanded: boolean;
  icon: React.ReactNode;
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left ${className}`}
    >
      <div className="flex items-center min-w-0">
        {icon}
        <span className="font-semibold text-gray-800 truncate">{title}</span>
        <span className="ml-2 text-sm text-gray-500 flex-shrink-0">
          ({count} order{count !== 1 ? "s" : ""})
        </span>
      </div>
      {isExpanded ? (
        <ChevronUpIcon size={20} className="text-gray-500 flex-shrink-0" />
      ) : (
        <ChevronDownIcon size={20} className="text-gray-500 flex-shrink-0" />
      )}
    </button>
  );
}

export function DeliveryOrderGroupSections<T>({
  partition,
  expandedSlots,
  onToggleSlot,
  renderOrder,
  sections = "all",
  dailySectionClassName = "mb-4",
  storeSectionClassName = "mb-3",
}: SlotSectionProps<T>) {
  const { dailyOrders, storeSlotEntries } = partition;
  const storeOrderCount = storeSlotEntries.reduce(
    (sum, [, list]) => sum + list.length,
    0
  );

  const showDaily = sections === "all" || sections === "daily";
  const showStore = sections === "all" || sections === "store";

  if (
    (showDaily ? dailyOrders.length : 0) === 0 &&
    (showStore ? storeSlotEntries.length : 0) === 0
  ) {
    return null;
  }

  const storeExpanded = expandedSlots.has(STORE_ORDERS_LABEL);

  return (
    <>
      {showDaily && dailyOrders.length > 0 && (
        <div
          className={
            sections === "daily"
              ? "space-y-3"
              : `border border-gray-200 rounded-lg overflow-hidden ${dailySectionClassName}`
          }
        >
          {sections !== "daily" && (
            <CollapsibleHeader
              title={DAILY_ORDERS_LABEL}
              count={dailyOrders.length}
              isExpanded={expandedSlots.has(DAILY_ORDERS_LABEL)}
              onClick={() => onToggleSlot(DAILY_ORDERS_LABEL)}
              icon={
                <PackageIcon
                  size={18}
                  className="text-emerald-600 mr-2 flex-shrink-0"
                  aria-hidden
                />
              }
            />
          )}
          {(sections === "daily" || expandedSlots.has(DAILY_ORDERS_LABEL)) && (
            <div
              className={
                sections === "daily" ? "space-y-3" : "p-3 pt-0 space-y-3 bg-white"
              }
            >
              {dailyOrders.map((item) => renderOrder(item))}
            </div>
          )}
        </div>
      )}

      {showStore && storeSlotEntries.length > 0 && (
        <div
          className={`${sections === "store" ? "space-y-2" : `border border-gray-200 rounded-lg overflow-hidden ${storeSectionClassName}`}`}
        >
          {sections !== "store" && (
            <CollapsibleHeader
              title={STORE_ORDERS_LABEL}
              count={storeOrderCount}
              isExpanded={storeExpanded}
              onClick={() => onToggleSlot(STORE_ORDERS_LABEL)}
              icon={
                <StoreIcon
                  size={18}
                  className="text-orange-600 mr-2 flex-shrink-0"
                  aria-hidden
                />
              }
            />
          )}
          {(sections === "store" || storeExpanded) && (
            <div
              className={
                sections === "store"
                  ? "space-y-2"
                  : "px-3 pb-3 pt-1 space-y-2 bg-white border-t border-gray-100"
              }
            >
              {storeSlotEntries.map(([slot, ordersInSlot]) => {
                const slotKey = storeSlotExpandKey(slot);
                const slotExpanded = expandedSlots.has(slotKey);
                return (
                  <div
                    key={slot}
                    className="border border-orange-100 rounded-lg overflow-hidden"
                  >
                    <button
                      type="button"
                      onClick={() => onToggleSlot(slotKey)}
                      className="w-full flex items-center justify-between px-3 py-2.5 bg-orange-50/80 hover:bg-orange-50 text-left"
                    >
                      <div className="flex items-center min-w-0">
                        <ClockIcon
                          size={16}
                          className="text-orange-600 mr-2 flex-shrink-0"
                          aria-hidden
                        />
                        <span className="text-sm font-medium text-gray-800 truncate">
                          {slot}
                        </span>
                        <span className="ml-2 text-xs text-gray-500 flex-shrink-0">
                          ({ordersInSlot.length})
                        </span>
                      </div>
                      {slotExpanded ? (
                        <ChevronUpIcon
                          size={18}
                          className="text-gray-500 flex-shrink-0"
                        />
                      ) : (
                        <ChevronDownIcon
                          size={18}
                          className="text-gray-500 flex-shrink-0"
                        />
                      )}
                    </button>
                    {slotExpanded && (
                      <div className="p-2 pt-0 space-y-2 bg-white">
                        {ordersInSlot.map((item) => renderOrder(item))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </>
  );
}
